export const PRODUCT_FAQS = [
  { label: "Size & Fit", key: "size_fit", body: "All Lalita Soujanya pieces are made in India and follow our house sizing. If between sizes, we recommend the larger. Custom sizing is available on request — please write to the studio with your measurements." },
  { label: "Customization", key: "custom", body: "Customization requests may be accommodated subject to design feasibility and fabric availability. Customized orders require full advance payment and are non-cancellable, non-returnable and non-refundable once confirmed." },
  { label: "Shipping", key: "shipping", body: "Ready pieces ship within 5–7 business days across India. International shipping is arranged on request via the studio. WhatsApp us for a personalised quote and timeline." },
  { label: "Care", key: "care", body: "Dry-clean only. Store in the muslin bag provided. Avoid direct sunlight and moisture. Keep embellished pieces flat when not in use." },
];

const compact = (value) => String(value || "").replace(/\s+/g, " ").trim();

export function buildProductSeo(product, absoluteImages = [], siteUrl = "https://lalitasoujanya.com") {
  const productUrl = `${siteUrl}/product/${product.id}`;
  const category = compact(product.category) || "occasion wear";
  const subcategory = compact(product.subcategory);
  const summary = compact(product.short_description || product.description);
  const description = `${product.name}, designer ${subcategory || category} from our Hyderabad atelier. ${summary || "Sizing, customisation and delivery support for Telangana and Andhra Pradesh."}`.slice(0, 160);
  const sizes = Array.isArray(product.sizes) ? product.sizes.join(", ") : "";
  const colors = Array.isArray(product.colors) ? product.colors.join(", ") : "";
  const deliveryDays = Number(product.delivery_time_days) || 7;
  const price = product.offer_price ?? product.original_price;
  const keywordParts = [
    product.name,
    subcategory,
    category,
    "designer wear Hyderabad",
    "occasion wear Hyderabad",
    "Telangana designer clothing",
    "Andhra Pradesh designer wear",
    ...(Array.isArray(product.tags) ? product.tags : []),
  ].filter(Boolean);

  const productNode = {
    "@type": "Product",
    "@id": `${productUrl}#product`,
    url: productUrl,
    name: product.name,
    description: compact(product.description) || description,
    image: absoluteImages,
    sku: product.slug || product.id,
    category: [category, subcategory].filter(Boolean).join(" > "),
    brand: { "@id": `${siteUrl}/#organization` },
    additionalProperty: [
      sizes && { "@type": "PropertyValue", name: "Available sizes", value: sizes },
      colors && { "@type": "PropertyValue", name: "Available colours", value: colors },
      { "@type": "PropertyValue", name: "Estimated delivery", value: `${deliveryDays} days` },
      { "@type": "PropertyValue", name: "Quality assurance", value: "Quality tested by the Lalita Soujanya atelier" },
      { "@type": "PropertyValue", name: "Authenticity", value: "100% genuine Lalita Soujanya product" },
    ].filter(Boolean),
  };

  if (price != null) {
    productNode.offers = {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "INR",
      price: Number(price),
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@id": `${siteUrl}/#store` },
      eligibleRegion: { "@type": "Country", name: "India" },
    };
  }

  return {
    title: `${product.name} | Designer ${subcategory || category} Hyderabad`,
    description,
    keywords: [...new Set(keywordParts)].join(", "),
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        productNode,
        {
          "@type": "ClothingStore",
          "@id": `${siteUrl}/#store`,
          name: "Lalita Soujanya Hyderabad Atelier",
          url: siteUrl,
          parentOrganization: { "@id": `${siteUrl}/#organization` },
          address: {
            "@type": "PostalAddress",
            streetAddress: "Nizampet Main Rd, Survey 37, Nagarjuna Homes, Nizampet",
            addressLocality: "Hyderabad",
            addressRegion: "Telangana",
            postalCode: "500085",
            addressCountry: "IN",
          },
          areaServed: [
            { "@type": "City", name: "Hyderabad" },
            { "@type": "State", name: "Telangana" },
            { "@type": "State", name: "Andhra Pradesh" },
          ],
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${productUrl}#breadcrumb`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
            { "@type": "ListItem", position: 2, name: "Shop", item: `${siteUrl}/shop` },
            { "@type": "ListItem", position: 3, name: category, item: `${siteUrl}/shop/${product.category_slug || "collection"}` },
            { "@type": "ListItem", position: 4, name: product.name, item: productUrl },
          ],
        },
        {
          "@type": "FAQPage",
          "@id": `${productUrl}#questions`,
          mainEntity: PRODUCT_FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.label,
            acceptedAnswer: { "@type": "Answer", text: faq.body },
          })),
        },
      ],
    },
  };
}
